import type { JSX } from "react";

import type { StateObject } from "@uirouter/core";
import type { StateDeclaration, StateOrName } from "@uirouter/react";
import toString from "lodash/toString";
import styled from "styled-components";

import Sentry from "@similarweb/sentry-logger";
import type { IAutocompleteAppItemDetails, IAutocompleteWebsiteItemDetails, ISite } from "@similarweb/ui-components";
import {
    AutocompleteAppItem,
    AutocompleteItemText,
    AutocompleteWebsiteItem,
    ListItemWebsite,
    ListItemKeyword,
} from "@similarweb/ui-components";

import type { IAutoCompleteAppItem } from "app/@types/autocomplete";
import { swSettings } from "app/common/services/swSettings";
import type { Recent } from "app/custom-hooks/useRecentsHook";
import { i18nFilter } from "app/filters/i18nFilter";
import { AppStoreName } from "app/pages/app-intelligence/constants";
import { transformRecentAnalysis } from "app/pages/start-pages/BaseAnalysisStartPageContainer";
import CountryService from "app/services/CountryService";
import { DefaultFetchService } from "app/services/fetchService";
import { RecentService } from "app/services/recent/recentService";
import type { ISerializable, serializeItems } from "app/services/solutions2Services/HomepageDataFetchers/utils";
import { dedupRecent } from "app/services/solutions2Services/HomepageDataFetchers/utils";
import { allTrackers } from "app/services/track/track";
import { proNavigator } from "single-spa/react-app/ProNavigator";

import type { IAppRecent, IWebsiteRecent, RecentsTypes, RecentTypeToPageStateName } from "./HomepageDataFetcherTypes";

const fetchService = DefaultFetchService.getInstance();
const translate = i18nFilter();

enum ItemType {
    Recent = "homepage.recent.item",
    SearchResult = "homepage.search.result.item",
}

enum EntityType {
    Website = "WEBSITE",
    App = "APP",
    Keyword = "KEYWORD",
}

const AutocompleteAppItemStyled = styled.div`
    ${AutocompleteItemText} {
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    }

    .ItemIcon {
        flex-shrink: 0;
    }
`;

const onClickRedirect = (
    url: string,
    itemId: string,
    type: EntityType,
    itemType: ItemType,
    onItemClick?: (key: string) => void,
    shouldNavigate?: (key: string) => boolean,
): void => {
    allTrackers.dispatchTrackEvent(
        "Search Bar",
        "click",
        `${itemType === ItemType.Recent ? "recent" : "search_result"}/${type || ""}/${itemId || ""}`,
    );
    if (typeof onItemClick === "function") {
        onItemClick(itemId);
    }
    if (typeof shouldNavigate === "function") {
        if (shouldNavigate(itemId)) {
            window.location.href = url;
        }
    } else {
        window.location.href = url;
    }
};

export const getAppResults = async (query: string, size = 10): Promise<IAutoCompleteAppItem[]> => {
    return await fetchService.get<IAutoCompleteAppItem[]>("/autocomplete/apps", {
        size,
        term: query,
        validate: true,
    });
};

export const getAppMultipleResults = async (query: string, size = 10): Promise<IAutoCompleteAppItem[]> => {
    try {
        const googleRequest = fetchService.get<IAutoCompleteAppItem[]>("/autocomplete/apps", {
            size,
            term: query,
            validate: true,
            store: AppStoreName.GOOGLE,
        });
        const appleRequest = fetchService.get<IAutoCompleteAppItem[]>("/autocomplete/apps", {
            size,
            term: query,
            validate: true,
            store: AppStoreName.APPLE,
        });

        const res = await Promise.all([googleRequest, appleRequest]);

        return [...res[0], ...res[1]];
    } catch (error) {
        Sentry.error("Error fetching App Analysis search result", error);

        return [];
    }
};

export const getWebsiteResults = async (query: string, size = 9): Promise<ISite[]> => {
    return fetchService.get<ISite[]>(
        `/autocomplete/websites?size=${size}&term=${query}&webSource=Desktop&validate=true`,
        null,
        { numberOfRetry: 2 },
    );
};

export interface IAdIntelligenceSite {
    name: string;
    favicon: string;
}

interface IAdIntelligenceAutocompleteResults {
    advertisers: IAdIntelligenceSite[];
    publishers: IAdIntelligenceSite[];
}

export const getAdIntelligenceResults = async (query: string): Promise<IAdIntelligenceAutocompleteResults> => {
    return fetchService.get(`/api/adIntelligence/entities?key=${query}`, null, { numberOfRetry: 2 });
};

export const getKeywordResults = async (query: string): Promise<ISite[]> => {
    return fetchService.get<ISite[]>(
        `/autocomplete/keywords?size=20&term=${query}&webSource=Desktop&validate=true`,
        null,
        { numberOfRetry: 2 },
    );
};

export const getRecentsAnalysis = async (
    filter?: RecentsTypes | RecentsTypes[],
    dedupe = false,
    dedupeFunction?: typeof serializeItems,
) => {
    const recentSearches = RecentService.getRecents();
    let recentFiltered = recentSearches;
    if (Array.isArray(filter)) {
        // @ts-expect-error FIXME: Type '"virtual"' is not assignable to type 'RecentsTypes'
        recentFiltered = recentSearches.filter((item) => filter.includes(item.data.type));
    } else if (filter) {
        recentFiltered = recentSearches.filter((item) => item.data.type === filter);
    }
    if (recentFiltered.length === 0) {
        return [];
    }

    if (dedupe) {
        // @ts-expect-error FIXME: Type 'ISerializable[]' is not assignable to type 'IRecents'
        recentFiltered = dedupRecent(recentFiltered, dedupeFunction);
    }

    return transformRecentAnalysis(recentFiltered, CountryService, proNavigator, translate);
};

export const getRecentsByParentState = (
    parentState: StateOrName | StateOrName[],
    dedupe = false,
    dedupeFunction?: typeof serializeItems,
) => {
    const recentSearches = RecentService.getRecents();
    const parentStatesFormatted = Array.isArray(parentState) ? parentState : [parentState];

    const hasCommonParentState = (state: StateDeclaration | StateObject) => {
        // @ts-ignore FIXME: strict migration - 'string | undefined' not assignable to 'StateOrName' — state.name is optional in @uirouter/react types but always set in practice
        if (parentStatesFormatted.includes(state.name)) {
            return true;
        }
        if (state.parent) {
            return hasCommonParentState(proNavigator.getState(state.parent));
        } else if (
            // if the state is not a parent state, check if it starts with any of the parent states
            parentStatesFormatted.some((parent) =>
                // @ts-ignore FIXME: strict migration - 'state.name' possibly 'undefined' — @uirouter/react types it as optional but always set in practice
                state.name.startsWith(typeof parent === "string" ? parent : parent.name),
            )
        ) {
            return true;
        }
        return false;
    };

    const filteredRecents = recentSearches.filter((recent) => {
        const stateName = recent.data.stateName;
        if (!stateName || !proNavigator.getState(stateName)) {
            return false;
        }
        return hasCommonParentState(proNavigator.getState(stateName));
    });

    if (dedupe) {
        return dedupRecent(filteredRecents as ISerializable[], dedupeFunction) as Recent[];
    }

    return transformRecentAnalysis(
        filteredRecents as ISerializable[],
        CountryService,
        proNavigator,
        translate,
    ) as Recent[];
};

export const generateRecents = (
    items: Array<IAppRecent | IWebsiteRecent>,
    recentTypeToPageStateMapping: RecentTypeToPageStateName,
    separator?: JSX.Element,
    defaultWebsiteStateParams: Record<string, string> = {},
    maxCompareItemsToDisplay?: number,
    onItemClick?: (key: string) => void,
    shouldNavigate?: (key: string) => boolean,
): JSX.Element[] => {
    return items.reduce(
        (results, item, index) => {
            // each recent item type should have a unique page state name (since each item
            // type should lead to a different page. the recentTypeToPageStateMapping argument enables
            // the user (developer) to set which recent type leads to which page. (see usage examples to understand more)
            const itemPageStateName = recentTypeToPageStateMapping[item.data.type];

            switch (item.data.type) {
                case "website":
                    results.push(
                        transformRecentWebsite(
                            item as IWebsiteRecent,
                            itemPageStateName,
                            defaultWebsiteStateParams,
                            maxCompareItemsToDisplay,
                            onItemClick,
                            shouldNavigate,
                        ),
                    );
                    break;
                case "app":
                    results.push(
                        transformRecentApp(item as IAppRecent, itemPageStateName, index, maxCompareItemsToDisplay),
                    );
                    break;
            }
            return results;
        },
        separator ? [separator] : [],
    );
};

export const getTransformedItem = (
    item: IWebsiteRecent,
    websitePageStateName: string,
    additionalParams: object = {},
) => {
    const category =
        item.data.category &&
        item.data.category.replace(/([/\s])/g, (match, curr) => {
            switch (curr) {
                case "/":
                    return "~";
                default:
                    return "_";
            }
        });

    const params = {
        duration: item.data.duration,
        country: item.data.country,
        category,
        key: `${item.data.mainItem}${
            item.data.comparedItems && item.data.comparedItems.length > 0 ? "," + toString(item.data.comparedItems) : ""
        }`,
        isWWW: "*",
        ...additionalParams,
    };

    const mainWebsite: IAutocompleteWebsiteItemDetails = {
        domain: item.data.mainItem,
        iconUrl: item.data.resources.mainItemFavIcon,
    };

    let compareItems: IAutocompleteWebsiteItemDetails[] | undefined;
    if (item.data.comparedItems && item.data.comparedItems.length > 0) {
        compareItems = item.data.comparedItems.map((comp, idx) => ({
            domain: comp,
            iconUrl: item.data.resources.comparedItemsFavicon[idx],
        }));
    }
    const url = proNavigator.href(websitePageStateName, params);

    return { mainWebsite, compareItems, category, params, url };
};

const transformRecentWebsite = (
    item: IWebsiteRecent,
    websitePageStateName: string,
    websitePageParams: Record<string, string>,
    maxCompareItemsToDisplay?: number,
    onItemClick?: (key: string) => void,
    shouldNavigate?: (key: string) => boolean,
): JSX.Element => {
    const { mainWebsite, compareItems, params, url } = getTransformedItem(
        item,
        websitePageStateName,
        websitePageParams,
    );

    return (
        <AutocompleteAppItemStyled>
            <AutocompleteWebsiteItem
                maxCompareItemsToDisplay={maxCompareItemsToDisplay}
                mainSite={mainWebsite}
                compareSites={compareItems}
                itemUrl={url}
                onItemClick={onClickRedirect.bind(
                    null,
                    url,
                    params.key,
                    EntityType.Website,
                    ItemType.Recent,
                    onItemClick,
                    shouldNavigate,
                )}
            />
        </AutocompleteAppItemStyled>
    );
};

const transformRecentApp = (
    item: IAppRecent,
    appPageStateName: string,
    index: number,
    maxCompareItemsToDisplay?: number,
): JSX.Element => {
    const params = {
        duration: item.data.duration,
        country: item.data.country,
        appId: `${item.data.appStore}_${item.data.mainItem}${
            item.data.comparedItems && item.data.comparedItems.length > 0 ? "," + toString(item.data.comparedItems) : ""
        }`,
    };
    // sometimes the recent data that has been retrieved from the server is in a different structure: the mainItem was a string
    // and the new mainItem is object.
    // the following weird condition is for backward compatibility support.
    // @ts-ignore FIXME: strict migration - else-branch has 'iconUrl: undefined' but IAutocompleteAppItemDetails.iconUrl is required string — backend should backfill missing favicon (see comment above)
    const mainApp: IAutocompleteAppItemDetails =
        typeof item.data.resources.mainItem === "object"
            ? {
                  app: item.data.resources.mainItem.title,
                  iconUrl: item.data.resources.mainItem.icon,
                  appStore: item.data.resources.mainItem.appStore,
              }
            : {
                  app: item.data.mainItem,
                  // a temporary undefined due to a missing data, in the future the backend should enrich the 'old' recent data with the favicon
                  iconUrl: undefined,
                  appStore: item.data.appStore === 0 ? "Google" : "Apple",
              };

    let compareItems: IAutocompleteAppItemDetails[] | undefined;
    if (item.data.comparedItems && item.data.comparedItems.filter((comparedItem) => comparedItem).length > 0) {
        if (typeof item.data.resources.comparedItems[0] !== "object") {
            compareItems = item.data.comparedItems
                .filter((comparedItem) => comparedItem)
                .map((comp, idx) => ({
                    app: item.data.comparedItems[idx]?.title,
                    iconUrl: item.data.comparedItems[idx]?.icon,
                    appStore: item.data.comparedItems[idx]?.appStore,
                }));
        } else {
            compareItems = item.data.comparedItems
                .filter((comparedItem) => comparedItem)
                .map((comp, idx) => ({
                    app: item.data.resources.comparedItems[idx]?.title,
                    iconUrl: item.data.resources.comparedItems[idx]?.icon,
                    appStore: item.data.resources.comparedItems[idx]?.appStore,
                }));
        }
    }
    const url = proNavigator.href(appPageStateName, params);

    return (
        <AutocompleteAppItemStyled>
            <AutocompleteAppItem
                maxCompareItemsToDisplay={maxCompareItemsToDisplay}
                key={`${mainApp.app}_${index}_${compareItems && compareItems?.join("")}`}
                mainItem={mainApp}
                compareItems={compareItems}
                itemUrl={url}
                // @ts-ignore FIXME: strict migration - bound function signature mismatches AutocompleteAppItem.onItemClick — partial application leaves optional params, runtime call is compatible
                onItemClick={onClickRedirect.bind(null, url, params.appId, EntityType.App, ItemType.Recent)}
            />
        </AutocompleteAppItemStyled>
    );
};

const capitalLetter = (str: string): string => {
    return `${str.charAt(0).toUpperCase()}${str.slice(1)}`;
};

export const createMixedSearchResults = (
    items: Array<(ISite & { type: string }) | IAutoCompleteAppItem>,
    appPageStateName: string,
    websitePageStateName: string,
    keywordPageStateName?: string,
    onItemClick?: (key: string) => void,
    shouldNavigate?: (key: string) => boolean,
): JSX.Element[] => {
    // @ts-ignore FIXME: strict migration - '(Element | undefined)[]' not assignable to 'Element[]' — switch covers all known runtime values, undefined branch is unreachable
    return items.map((item) => {
        switch (item.type) {
            case "app":
                return createAppSearchResultItem(item as IAutoCompleteAppItem, appPageStateName);
            case "website":
                return createWebsiteSearchResultItem(
                    item as ISite,
                    websitePageStateName,
                    undefined,
                    onItemClick,
                    shouldNavigate,
                );
            case "keyword":
                // @ts-ignore FIXME: strict migration - 'string | undefined' not assignable to 'string' — keywordPageStateName is optional in createMixedSearchResults but required by createKeywordSearchResultItem
                return createKeywordSearchResultItem(item as ISite, keywordPageStateName);
        }
    });
};

export const createAppSearchResultItem = (item: IAutoCompleteAppItem, appPageStateName: string): JSX.Element => {
    let defaultParams = {};
    let appDefaultParams = {};
    try {
        const stateObject = proNavigator.getState(appPageStateName);
        const configId = proNavigator.getConfigId(stateObject);
        // @ts-ignore FIXME: strict migration - 'null' cannot be used as an index type — proNavigator.getConfigId may return null, swSettings.components is keyed by string
        defaultParams = swSettings.components[configId]?.defaultParams;
        appDefaultParams = {
            ...defaultParams,
            appId: item.appKey,
        };
    } catch (ex) {}
    const appDetails: IAutocompleteAppItemDetails = {
        // @ts-ignore FIXME: strict migration - 'string | undefined' not assignable to 'string' — ISite.image is optional but IAutocompleteAppItemDetails.iconUrl required
        iconUrl: item.image,
        app: item.name,
        appStore: capitalLetter(item.store) as IAutocompleteAppItemDetails["appStore"],
    };
    const url = proNavigator.href(appPageStateName, appDefaultParams);

    return (
        <AutocompleteAppItemStyled>
            <AutocompleteAppItem
                key={item.appKey}
                mainItem={appDetails}
                itemUrl={url}
                // @ts-ignore FIXME: strict migration - bound function signature mismatches AutocompleteAppItem.onItemClick — partial application leaves optional params, runtime call is compatible
                onItemClick={onClickRedirect.bind(
                    null,
                    url,
                    `${appDetails.app}_${appDetails.appStore}`,
                    EntityType.App,
                    ItemType.SearchResult,
                )}
            />
        </AutocompleteAppItemStyled>
    );
};

export const createWebsiteSearchResultItem = (
    item: ISite,
    websitePageStateName: string,
    stateParams?: any,
    onItemClick?: (domain: string) => void,
    shouldNavigate: (domain: string) => boolean = (domain: string) => true,
): JSX.Element => {
    const { name, image } = item;
    let websiteDefaultParams = {};
    try {
        const stateObject = proNavigator.getState(websitePageStateName);
        const configId = proNavigator.getConfigId(stateObject);
        // @ts-ignore FIXME: strict migration - 'null' cannot be used as an index type — proNavigator.getConfigId may return null, swSettings.components is keyed by string
        websiteDefaultParams = swSettings.components[configId].defaultParams;
    } catch (e) {}
    const goto = () => {
        proNavigator.go(websitePageStateName, {
            ...websiteDefaultParams,
            ...stateParams,
            key: name,
            isWWW: "*",
        });
    };

    return (
        <ListItemWebsite
            key={`website.${name}`}
            // @ts-ignore FIXME: strict migration - 'string | undefined' not assignable to 'string' — ISite.image is optional but ListItemWebsite.img required
            img={image}
            text={name}
            onClick={() => {
                if (typeof onItemClick === "function") {
                    onItemClick(name);
                }
                allTrackers.dispatchTrackEvent(
                    "Search Bar",
                    "click",
                    `search_result/${EntityType.Website}/${name || ""}`,
                );
                shouldNavigate(name) && goto();
            }}
        />
    );
};

const createKeywordSearchResultItem = (item: ISite, keywordPageStateName: string): JSX.Element => {
    const { name } = item;
    const state = proNavigator.getState(keywordPageStateName);
    const configId = proNavigator.getConfigId(state);
    // @ts-ignore FIXME: strict migration - 'null' cannot be used as an index type — proNavigator.getConfigId may return null, swSettings.components is keyed by string
    const defaultParams = swSettings.components[configId].defaultParams;
    const url = proNavigator.href(keywordPageStateName, {
        ...defaultParams,
        keyword: item.name,
    });

    return (
        <ListItemKeyword
            key={`${name}`}
            iconName={"search-keywords"}
            text={name}
            onClick={onClickRedirect.bind(null, url, name, EntityType.Keyword, ItemType.SearchResult)}
        />
    );
};
